<template>
  <div
    class="race-list-pane"
    :class="[
      // レースを再生したい場合であっても誤ってSFgo’s Race Key Pointsを押してしまうことがあるため、SFgo’s Race Key Points分のスペースを常に確保する。
      { 'race-list-pane--has-information': true },
      { 'race-list-pane--show-information': showInformation },
      { 'race-list-pane--for-pc': forPc },
    ]"
  >
    <figure class="race-list-pane__bg">
      <img :src="championshipLogoPath" alt="" />
    </figure>

    <select-year-section
      class="race-list-pane__year-select"
      :class="{
        'race-list-pane__year-select--hidden': showInformation,
        'race-list-pane__year-select--timetable': showTimetable,
        'race-list-pane__year-select--for-pc': forPc,
      }"
      :raceYear="currentYear"
      :yearList="yearList"
      @change="yearChange"
    />
    <section
      class="race-list-pane__content"
      :class="{ 'race-list-pane__content--show-timetable': showTimetable }"
      :style="{ height: contentStyleHeight }"
    >
      <div class="race-list-pane__content-inner">
        <div class="race-list-pane__content-header" ref="header">
          <race-list-header-section
            v-if="currentChampionship"
            :currentChampionship="currentChampionship"
            :showInformation="showInformation"
            :show-timetable="showTimetable"
            @onClickViewTimetable="toggleMatchList"
            @onClickClose="toggleMatchList"
          />
        </div>

        <div class="race-list-pane__circuit-mode" v-if="showTimetable">
          <circuit-mode-section
            class="race-list-pane__circuit-mode-section"
            :hasDisplayedOnboarding="true"
          />
        </div>

        <div class="race-list-pane__timetable" v-if="showTimetable">
          <race-timetable-section :raceListByStartDate="raceListByStartDate" />
        </div>
      </div>

      <race-information-section
        v-if="hasCurrentRaceInformation"
        ref="raceListPaneInformation"
        class="race-list-pane__information"
        :showInformation="showInformation"
        @radioPlay="playRadioAudio"
      />
      <information-toggle-button-parts
        v-if="!showTimetable"
        class="race-list-pane__information-toggle-button"
        :hasCurrentRaceInformation="hasCurrentRaceInformation"
        :showInformation="showInformation"
        @click="toggleInformation"
      />

      <race-list-section
        class="race-list-pane__race-list"
        :championships="championships"
        :enabledRaceListCarousel="enabledRaceListCarousel"
        :currentChampionshipId="currentChampionship ? currentChampionship.id || '' : ''"
        @change="change"
      />
      <div class="race-list-pane__content-bg"></div>
    </section>
    <transition name="fade">
      <race-list-close-timetable-button-parts
        v-if="showTimetable"
        class="race-list-pane__timetable-close-button"
        @onClickClose="toggleMatchList"
      />
    </transition>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onUnmounted,
  reactive,
  ref,
  Ref,
  toRef,
  watch,
} from '@vue/composition-api'
import dayjs from 'dayjs'
import DeviceInfo from '@/util/DeviceInfo'
import SelectYearSection from '@/components/RaceListPage/RaceListPane/SelectYearSection.vue'
import RaceListSection from '@/components/RaceListPage/RaceListPane/RaceListSection.vue'
import RaceInformationSection from '@/components/RaceListPage/RaceListPane/RaceInformationSection.vue'
import InformationToggleButtonParts from '@/components/RaceListPage/RaceListPane/parts/InformationToggleButtonParts.vue'
import ChampionshipDocument from '@/store/stores/collectionModule/documents/championship/ChampionshipDocument'
import MessageDialogStore from '@/store/stores/pageStore/common/MessageDialogStore'
import StoreUtil from '@/store/StoreUtil'
import CloudFrontUtil from '@/util/aws/CloudFrontUtil'
import LocalCache from '@/util/localcache/LocalCache'
import Logger from '@/util/logger/Logger'
import RaceDocument from '@/store/stores/collectionModule/documents/race/RaceDocument'
import ContentsInfoDocument from '@/store/stores/collectionModule/documents/contents/ContentsInfoDocument'
import HighlightDocument from '@/store/stores/collectionModule/documents/highlight/HighlightDocument'
import { Response } from '@/store/stores/collectionModule/CollectionTypes'
import useRealtimeMessaging, { RTMCallbackParamType } from '@/components/hook/useRealtimeMessaging'
import useRacePointRadio from '@/components/RaceListPage/hook/useRacePointRadio'
import RadioDataDocument from '@/store/stores/collectionModule/documents/Radio/RadioDataDocument'
import CircuitModeSection from '@/components/HomePage/HomePane/CircuitModeSection.vue'
import RaceTimetableSection from '@/components/RaceListPage/RaceListPane/RaceTimetableSection.vue'
import RaceListHeaderSection from '@/components/RaceListPage/RaceListPane/RaceListHeaderSection.vue'
import RaceListCloseTimetableButtonParts from '@/components/RaceListPage/RaceListPane/parts/RaceListCloseTimetableButtonParts.vue'

/**
 * レース一覧画面ペインのコンポーネント
 */
export default defineComponent({
  name: 'RaceListPane',
  components: {
    RaceListCloseTimetableButtonParts,
    RaceListHeaderSection,
    RaceTimetableSection,
    CircuitModeSection,
    SelectYearSection,
    RaceListSection,
    RaceInformationSection,
    InformationToggleButtonParts,
  },
  props: {
    /**
     * RaceInfoリンクボタン表示
     */
    enabledRaceInfo: {
      type: Boolean,
      default: false,
    },
    /**
     * レース一覧カルーセル
     */
    enabledRaceListCarousel: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    // TopPageStore
    const topPageStore = StoreUtil.useStore('TopPageStore')
    // NotificationStore
    const notificationStore = StoreUtil.useStore('NotificationStore')

    const {
      fetchTopPageData,
      currentChampionship,
      sortedChampionships,
      highlightsForPreLoad,
      contentsInfo,
      qualifyingRaceStartEventList,
      clearTopPageData,
      fetchRaceRankData,
      getRankingCardData,
      clearRankData,
      fetchRacePointVideoData,
      fetchRacePointRadioData,
      racePointRadioData,
      racePointVideoData,
      radioDataStore,
    } = topPageStore
    const { fetchNotificationData } = notificationStore

    const header = ref<HTMLElement | null>(null)
    const state = reactive({
      contentHeight: 'auto',
    })
    const forPc = DeviceInfo.isForPC()

    // リアルタイムメッセージ
    const { initRTM, subscribeRTM, unSubscribeRTM } = useRealtimeMessaging()
    // 無線
    const { playRadioDataAudio } = useRacePointRadio()

    /**
     * 大会ロゴ画像の署名URLを取得する
     */
    const championshipLogoPath = computed(() =>
      CloudFrontUtil.getSignedUrl(topPageStore.currentChampionship?.value?.championshipLogo),
    )

    /**
     * レースにInformation情報が含まれるか
     */
    const hasCurrentRaceInformation = ref<boolean>(false)

    /**
     * Information 表示トグル
     */
    const showInformation = ref<boolean>(false)
    const toggleInformation = () => {
      showInformation.value = !showInformation.value
    }

    /**
     * コンテンツの高さを設定する
     */
    const contentStyleHeight = computed(() => {
      if (topPageStore.topPageState.showTimetable) {
        return '100%'
      }

      return state.contentHeight
    })

    /**
     * アニメーション用にコンテンツの高さを制御
     */
    const manageContentHeight = () => {
      if (!header.value) {
        state.contentHeight = 'auto'
        return
      }
      const headerElmRect = header.value.getBoundingClientRect()
      if (forPc) {
        state.contentHeight = `${
          236 + (window.innerHeight - 48 - 44 - 236) / 2 + headerElmRect.height / 2
        }px`
      } else {
        state.contentHeight = `${headerElmRect.height + 52 + 185}px`
      }
    }

    /**
     * 全てのセッションの表示/非表示を切り替える
     */
    const toggleMatchList = () => {
      topPageStore.toggleTimetable()
      nextTick(() => {
        manageContentHeight()
      })
    }

    /**
     * 開始日ごとに分類されたレース情報を取得する
     */
    const getRaceListByStartDate = (races: Array<RaceDocument>) =>
      races.reduce((acc, cur) => {
        const key = cur.startDate ? dayjs(cur.startDate).tz('Asia/Tokyo').format('YYYY-MM-DD') : ''
        if (!acc[key]) {
          acc[key] = [cur]
        } else {
          acc[key].push(cur)
        }
        return acc
      }, {} as Record<string, Array<RaceDocument>>)

    /**
     * 開始日ごとに分類された全レース一覧
     */
    const raceListByStartDate = computed(() => {
      const allRaces = [
        ...topPageStore.sessionsWithOutRace.value,
        ...topPageStore.mainRace.value,
      ].sort((a, b) => (a.startDate || 0) - (b.startDate || 0))
      // 開始日時が存在するレース
      const racesWithStartDate = getRaceListByStartDate(allRaces.filter((race) => race.startDate))
      // 開始日時が存在しないレース
      const racesWithoutStartDate = getRaceListByStartDate(
        allRaces.filter((race) => !race.startDate),
      )
      // 開始日時が存在するレースを先に表示する
      return { ...racesWithStartDate, ...racesWithoutStartDate }
    })

    /**
     * Information表示状態を監視し、Informationを非表示にした場合に以下の処理を行う
     * 無線交信データの音声の再生を停止
     * スクロール位置をリセット
     */
    watch(
      () => showInformation.value,
      (newShowInformation) => {
        if (!newShowInformation) {
          // 無線交信データの音声の再生を停止
          radioDataStore.pauseRadioAudio()

          // スクロール位置をリセット
          const showInformationElement = document.querySelector(
            '.race-information-section',
          ) as HTMLDivElement
          showInformationElement.scrollTop = 0
        }
      },
    )

    watch(currentChampionship, () => {
      nextTick(() => {
        manageContentHeight()
      })
    })

    onUnmounted(() => {
      // レース、お知らせに関連するリアルタイムメッセージ受信を停止
      unSubscribeRTM()
      clearRankData()
      radioDataStore.clearRadioData()
      radioDataStore.clearAudioPlayer()
    })

    return {
      header,
      state,
      forPc,
      contentStyleHeight,
      currentYear: toRef(topPageStore.topPageState, 'raceYear'),
      yearList: toRef(topPageStore.topPageState, 'yearList'),
      fetchTopPageData,
      currentChampionship: currentChampionship as Ref<ChampionshipDocument>,
      championshipLogoPath,
      championships: sortedChampionships as Ref<Array<ChampionshipDocument>>,
      fetchRaces: topPageStore.fetchRaces,
      fetchDisplayedChampionshipMovieInfo: topPageStore.fetchDisplayedChampionshipMovieInfo,
      qualifyingRaces: topPageStore.qualifyingRaceList,
      sessionsWithOutRace: topPageStore.sessionsWithOutRace as Ref<Array<RaceDocument>>,
      finalRaces: topPageStore.mainRace as Ref<Array<RaceDocument>>,
      raceListByStartDate,
      clearTopPageData,
      highlightsForPreLoad,
      contentsInfo,
      qualifyingRaceStartEventList,
      initRTM,
      subscribeRTM,
      unSubscribeRTM,
      showTimetable: toRef(topPageStore.topPageState, 'showTimetable'),
      toggleMatchList,
      hasCurrentRaceInformation,
      toggleInformation,
      showInformation,
      fetchRaceRankData,
      getRankingCardData,
      clearRankData,
      playRadioDataAudio,
      fetchRacePointVideoData,
      racePointVideoData,
      fetchRacePointRadioData,
      racePointRadioData,
      radioDataStore,
      fetchNotificationData,
    }
  },
  async created() {
    this.radioDataStore.createAudioPlayer()

    // 初期表示は本年のレース情報を表示する
    await this.fetchTargetYearRaceData()
    this.preLoadRaceMovieData()
    // レースに関連するリアルタイムメッセージを受信
    // (予選のQ1-A,Q1-B,Q2 のスタートイベントが登録された場合にレース一覧に反映するため)
    await this.initRTM()
    this.subscribeRTM('highlight', async (data: RTMCallbackParamType) => {
      if (this.$router.currentRoute.name === 'RaceVideoPage') return
      Logger.debug(
        `RaceListPane#subscribeRTM: Receive highlight event. event: ${JSON.stringify(data)}`,
      )
      await this.fetchTopPageData(this.currentYear)
      this.fetchDisplayedChampionshipMovieInfo()
      this.preLoadRaceMovieData()
    })
    // お知らせのリアルタイムメッセージを受信
    this.subscribeRTM('information', async (data: RTMCallbackParamType) => {
      Logger.debug(
        `RaceListPane#subscribeRTM: Receive notification event. event: ${JSON.stringify(data)}`,
      )
      this.fetchNotificationData()
    })
  },
  methods: {
    /**
     * 指定された年度のTOPページのデータを取得する。
     * @param raceYear 年度
     * @return 処理完了を待機するためのPromise
     */
    async fetchTargetYearRaceData(raceYear?: number) {
      this.clearTopPageData()
      const results = await this.fetchTopPageData(raceYear)
      const failedResponse = [...results].find((response) => !response.isSuccess)
      if (failedResponse) {
        await MessageDialogStore.value.open({
          title: this.$t('RaceListPage.errors.fetchTopPageDataError.title') as string,
          message: this.$t('RaceListPage.errors.fetchTopPageDataError.message') as string,
          errorApiResponse: failedResponse.response,
        })
      } else {
        this.preLoadRaceMovieData()
        await this.change(this.currentChampionship)
      }
    },
    /**
     * レース動画の開始位置と、予選の各スタートイベントの動画の先読みを行う。
     */
    preLoadRaceMovieData() {
      const preLoadItems = this.highlightsForPreLoad(
        this.qualifyingRaceStartEventList,
        this.contentsInfo,
      )
      LocalCache.preLoad(preLoadItems).then(() => {
        Logger.info('RaceListPane#created: Success to preload start event movie data.')
      })
    },
    /**
     * TOP画面で大会が選択された際に呼び出される。
     * 選択された大会のレース情報を取得する。
     * @param championship 変更後の大会
     */
    async change(championship: ChampionshipDocument) {
      if (!championship || !championship.id) {
        return
      }
      this.clearRankData()

      /** レース切り替え時にInformation部を閉じる */
      this.showInformation = false
      /** レースにInformation情報が含まれるかのフラグをリセット */
      this.hasCurrentRaceInformation = false

      /** 選択されたレース大会に変更 */
      this.currentChampionship = championship
      const results = await this.fetchRaces(championship.id)
      const failedResponse = [...results].find(
        (response: Response<RaceDocument | ContentsInfoDocument | HighlightDocument>) =>
          !response.isSuccess,
      )
      if (failedResponse) {
        await MessageDialogStore.value.open({
          title: this.$tc('RaceListPage.errors.fetchRacesError.title'),
          message: this.$tc('RaceListPage.errors.fetchRacesError.message'),
          errorApiResponse: failedResponse.response,
        })
      } else {
        this.preLoadRaceMovieData()
      }

      const relatedRaceDataResults = await Promise.all([
        // レースのInformation情報を取得
        this.fetchRaceRankData(championship, this.finalRaces[0], this.qualifyingRaces[0]),
        this.fetchRacePointVideoData(this.finalRaces[0], this.sessionsWithOutRace),
        this.fetchRacePointRadioData(),
        // メイン映像とオンボード映像（と英語の中継映像）の動画情報を取得
        this.fetchDisplayedChampionshipMovieInfo(),
      ])

      const fetchMovieInfoFailed =
        relatedRaceDataResults?.[3] &&
        relatedRaceDataResults[3].flat().find((response) => !response.isSuccess)
      if (fetchMovieInfoFailed) {
        // 動画情報取得に失敗した場合再生ボタンを押せなくなるため、エラーを表示する
        await MessageDialogStore.value.open({
          title: this.$tc('RaceListPage.errors.fetchMovieInfoError.title'),
          message: this.$tc('RaceListPage.errors.fetchMovieInfoError.message'),
          errorApiResponse: fetchMovieInfoFailed?.response,
        })
      }

      this.hasCurrentRaceInformation =
        this.getRankingCardData.length > 0 ||
        this.racePointVideoData.length > 0 ||
        this.racePointRadioData.length > 0
    },
    /**
     * 年度が変更された場合に呼び出される。
     * 指定された年度の大会/レース情報を取得する。
     */
    yearChange(raceYear: number) {
      this.fetchTargetYearRaceData(raceYear)
    },
    /**
     * 無線交信データの音声を再生する。
     * @param radioData 無線交信データ
     */
    async playRadioAudio(radioData: RadioDataDocument) {
      await this.playRadioDataAudio(radioData)
    },
  },
})
</script>

<style lang="scss" scoped>
.race-list-pane {
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  width: 100%;
  height: calc(#{$mypageContentHeight} - #{env(safe-area-inset-bottom)});
  margin: 0 auto;
  text-align: center;
  background: #000;
  overflow-y: auto;
  z-index: 2;

  &::before {
    display: block;
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 80px;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, #000000 100%);
    z-index: 1;
  }

  &::after {
    position: fixed;
    top: $headerPaneHeight;
    bottom: calc(#{$globalNavigationHeight} + #{env(safe-area-inset-bottom)});
    right: 0;
    left: 0;
    display: block;
    content: '';
    width: 100%;
    background: rgba(#000, 0.2), rgba(#fff, 0.2);
    backdrop-filter: blur(2.5px);
    opacity: 0;
    z-index: 1;
    pointer-events: none;
    transition: all ease 0.3s;
  }

  &--show-modal {
    // 有料会員限定コンテンツモーダル表示時はモーダルが一番上の階層に表示される
    z-index: 20;
  }

  > * {
    z-index: 3;
  }

  &__bg {
    position: absolute;
    left: 0;
    top: 0;
    bottom: auto;
    width: 100%;
    height: calc(#{$mypageContentHeight} - #{env(safe-area-inset-bottom)} - 116px);
    z-index: 0;
    pointer-events: none;

    img {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center top;
    }
  }

  &__content {
    width: 100%;
    padding: 52px 0 123px;
    background: linear-gradient(180deg, rgba(247, 247, 247, 0) 0%, #f7f7f7 min(48vw, 180px)),
      linear-gradient(180deg, rgba(247, 247, 247, 0) 0%, #f7f7f7 min(48vw, 180px)),
      linear-gradient(180deg, rgba(247, 247, 247, 0) 0%, #f7f7f7 min(48vw, 180px)),
      linear-gradient(180deg, rgba(247, 247, 247, 0) 0%, #f7f7f7 min(48vw, 180px));
    transition: height 0.3s ease;

    &-inner {
      width: 100%;
    }

    &--show-timetable {
      position: relative;
      height: 100%;
      padding-top: 45px;
      margin-top: -44px;
      overflow-y: auto;
      background: rgba(#f7f7f7, 0.9);
    }
  }

  &__year-select {
    z-index: 4;
    transition: opacity 0.3s ease;

    &--hidden {
      opacity: 0;
      pointer-events: none;
    }

    &--timetable {
      &:not(.race-list-pane__year-select--for-pc) {
        color: $black;
      }
    }
  }

  &__btn-list {
    margin: 20px 0;
    transition: opacity 0.3s ease;

    &--hidden {
      opacity: 0;
      pointer-events: none;
    }
  }

  &__circuit-mode {
    padding: 20px 4px 0;
  }

  &__timetable {
    width: 100%;
    padding: 50px 30px 0;
  }

  &__information {
    position: fixed;
    right: 50%;
    height: 0;
    bottom: calc(#{$globalNavigationHeight} + #{env(safe-area-inset-bottom)});
    transform: translate(50%, 0);
    transition: all 0.5s ease;

    &-toggle-button {
      position: fixed;
      right: 50%;
      bottom: calc(142px + #{$globalNavigationHeight} + #{env(safe-area-inset-bottom)});
      transform: translate(50%, 0);
      z-index: 4;
    }
  }

  &__race-list {
    position: fixed;
    bottom: calc(#{$globalNavigationHeight} + #{env(safe-area-inset-bottom)});
    z-index: 6;
    margin-bottom: 14px;
    transition: opacity 0.3s ease;

    &--hidden {
      opacity: 0;
      pointer-events: none;
    }
  }

  &__timetable-close-button {
    z-index: 5;
  }

  &--has-information {
    .race-list-pane {
      &__content {
        padding: 52px 0 185px;

        &::after {
          position: fixed;
          left: 0;
          bottom: calc(#{$globalNavigationHeight} + #{env(safe-area-inset-bottom)});
          display: block;
          content: '';
          width: 100%;
          height: 160px;
          background: linear-gradient(180deg, rgba(#f7f7f7, 0) 0%, #f7f7f7 86px),
            linear-gradient(180deg, rgba(#f7f7f7, 0) 0%, #f7f7f7 96px);
          z-index: 3;
          pointer-events: none;
        }
      }
    }
  }

  &--show-information {
    &::after {
      opacity: 1;
    }

    .race-list-pane {
      &__data,
      &__btn-list {
        position: relative;
        z-index: 2;
      }

      &__bg {
        position: fixed;
        top: $globalNavigationHeight;
      }

      &__content {
        color: #fff;
        background: linear-gradient(
            180deg,
            rgba(247, 247, 247, 0) 0%,
            rgba(247, 247, 247, 0) min(48vw, 180px)
          ),
          linear-gradient(
            180deg,
            rgba(247, 247, 247, 0) 0%,
            rgba(247, 247, 247, 0) min(48vw, 180px)
          ),
          linear-gradient(
            180deg,
            rgba(247, 247, 247, 0) 0%,
            rgba(247, 247, 247, 0) min(48vw, 180px)
          ),
          linear-gradient(
            180deg,
            rgba(247, 247, 247, 0) 0%,
            rgba(247, 247, 247, 0) min(48vw, 180px)
          );

        &::after {
          height: 240px;
        }
      }

      &__information {
        height: $mypageContentHeight;
        bottom: calc(#{$globalNavigationHeight} + #{env(safe-area-inset-bottom)});
      }
    }
  }

  &--for-pc {
    justify-content: flex-start;

    .race-list-pane__content {
      padding: 0 60px;
      justify-content: flex-start;

      &.race-list-pane__content--show-timetable {
        justify-content: flex-start;
        width: 712px;
        padding-top: 50px;
        padding-right: 260px;
        overflow-y: hidden;

        &::before {
          position: absolute;
          top: 0;
          left: 0;
          z-index: -1;
          width: calc(100% - 200px);
          height: calc(100% + 300px);
          content: '';
          background: #e5e5e5;
        }

        .race-list-pane__content-inner {
          height: 100%;
          overflow-y: auto;
        }

        .race-list-pane__content-bg {
          position: absolute;
          top: 0;
          right: 0;
          width: 200px;
          height: calc(100% + 300px);
          background: linear-gradient(270deg, rgba(229, 229, 229, 0) 0%, #e5e5e5 100%);
        }
      }
    }

    .race-list-pane__circuit-mode {
      padding: 10px 0 0;

      .race-list-pane__circuit-mode-section {
        width: 100%;
        margin: 0;
      }
    }

    .race-list-pane__timetable {
      padding-right: 14px;
      padding-left: 14px;
    }
  }
}

@media screen and (max-height: $responsiveMinHeight) {
  .race-list-pane {
    &__btn-list {
      margin: 12px 0;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
